import { apiDelete, apiGet, apiGetBlob, apiPost } from "./client";

export type AccountingProviderEnvironment = "test" | "production";
export type AccountingConnectionStatus =
  | "active"
  | "reauthorization_required"
  | "error"
  | "disconnected";
export type AccountingWebhookStatus =
  | "not_configured"
  | "configuring"
  | "active"
  | "error"
  | "disabled";
export type AccountingBackgroundSyncStatus = "succeeded" | "partial" | "failed";
export type AccountingIntegrationAvailability =
  | "coming_soon"
  | "pilot"
  | "available"
  | "connected";

export interface AccountingConnection {
  id: string;
  workspaceId: string;
  companyId: string;
  providerKey: "tripletex";
  providerEnvironment: AccountingProviderEnvironment;
  status: AccountingConnectionStatus;
  externalCompanyId?: string | null;
  externalCompanyName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  authorizationVersion?: string | null;
  authorizedAt?: string | null;
  authorizedBy?: string | null;
  lastVerifiedAt?: string | null;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  webhookStatus: AccountingWebhookStatus;
  webhookUpdatedAt?: string | null;
  lastBackgroundSyncAt?: string | null;
  lastBackgroundSyncStatus?: AccountingBackgroundSyncStatus | null;
  lastBackgroundSyncErrorCode?: string | null;
  disconnectedAt?: string | null;
}

export interface AccountingIntegrationCatalogItem {
  providerKey: "tripletex";
  availability: AccountingIntegrationAvailability;
  providerEnvironment: AccountingProviderEnvironment;
  authorizationVersion?: string | null;
  connection?: AccountingConnection | null;
}

export interface ConnectTripletexPayload {
  companyId: string;
  employeeToken: string;
  providerEnvironment: AccountingProviderEnvironment;
  authorizationConfirmed: true;
  authorizationVersion: string;
}

export type AccountingRegistrationState =
  | "not_registered"
  | "pending"
  | "submitted"
  | "confirmed"
  | "failed";
export type AccountingSettlementStatus =
  | "open"
  | "partially_settled"
  | "settled"
  | "credited"
  | "overpaid";
export type AccountingRecoveryStage = "customer" | "order" | "invoice";

export interface AccountingInvoiceRecovery {
  stage: AccountingRecoveryStage | null;
  canRetry: boolean;
  inProgress: boolean;
  requiresReconciliation: boolean;
  readyAt: string | null;
  attemptCount: number;
}

export interface AccountingInvoiceActivity {
  type: "registration_confirmed" | "settlement_observed" | "settlement_changed";
  at: string | null;
  previousStatus?: AccountingSettlementStatus | null;
  status?: AccountingSettlementStatus | null;
  amountOutstanding?: string | null;
}

export interface AccountingInvoiceStatus {
  invoiceId: string;
  mode: "manual" | "integration" | null;
  providerKey: string | null;
  state: AccountingRegistrationState;
  externalDocument: {
    orderId: string | null;
    orderNumber: string | null;
    invoiceId: string | null;
    invoiceNumber: string | null;
    kid?: string | null;
  } | null;
  delivery?: {
    status: "unknown" | "not_sent" | "sent" | "failed";
    method: string | null;
    recipient: string | null;
    deliveredAt: string | null;
  } | null;
  settlement: {
    status: AccountingSettlementStatus | null;
    amount: string | null;
    amountOutstanding: string | null;
    observedAt: string | null;
  } | null;
  synchronization: {
    lastReconciledAt: string | null;
  } | null;
  recovery: AccountingInvoiceRecovery | null;
  activity?: AccountingInvoiceActivity[];
  error: {
    code: string | null;
    message: string | null;
    providerRequestId: string | null;
  } | null;
}

export interface AccountingInvoicePreview {
  mode: "preview";
  providerKey: "tripletex";
  connectionId: string;
  invoiceId: string;
  submissionKey: string;
  externalOrderNumber: string;
  payloadHash: string;
  currency: string;
  customer: {
    localCustomerId: string;
    organizationNumber: string;
    resolution: "mapped" | "matched" | "missing" | "would_create";
    externalCustomerId: string | null;
    externalCustomerNumber: string | null;
  };
  lines: Array<{
    description: string;
    quantity: string;
    unitPriceExcludingVat: string;
    discountPercent: string;
    vatRate: string;
    tripletexVatTypeId: string;
    tripletexVatTypeNumber: string;
  }>;
  totals: {
    excludingVat: string;
    vat: string;
    total: string;
  };
  existingSubmission: {
    state: AccountingRegistrationState;
    externalOrderId: string | null;
    externalInvoiceId: string | null;
    lastError: string | null;
    lastErrorMessage: string | null;
    lastProviderRequestId: string | null;
    recoveryStage: AccountingRecoveryStage | null;
    recoveryInProgress: boolean;
    externalSettlementStatus: AccountingSettlementStatus | null;
    externalSettlementObservedAt: string | null;
  } | null;
  canSubmit: boolean;
  recovery: AccountingInvoiceRecovery | null;
}

export interface AccountingInvoiceSubmission {
  mode: "submission";
  providerKey: string;
  connectionId: string;
  invoiceId: string;
  state: AccountingRegistrationState;
  submissionKey: string;
  externalOrderId: string | null;
  externalOrderNumber: string | null;
  externalInvoiceId: string | null;
  externalInvoiceNumber: string | null;
  externalAmount: string | null;
  externalAmountOutstanding: string | null;
  externalSettlementStatus: AccountingSettlementStatus | null;
  externalSettlementObservedAt: string | null;
  lastReconciledAt: string | null;
  lastError: string | null;
  lastErrorMessage: string | null;
  lastProviderRequestId: string | null;
  recovery: AccountingInvoiceRecovery;
  warnings: string[];
}

const connectionPath = "/api/accounting/connections";
const catalogPath = "/api/accounting/catalog";
const invoicePath = "/api/accounting/invoices";

export const getAccountingIntegrationCatalog = async (
  token: string,
  workspaceId: string,
  companyId: string,
): Promise<AccountingIntegrationCatalogItem[]> => {
  const response = await apiGet(
    token,
    `${catalogPath}?companyId=${encodeURIComponent(companyId)}`,
    workspaceId,
  );
  return (response?.data as AccountingIntegrationCatalogItem[]) ?? [];
};

export const listAccountingConnections = async (
  token: string,
  workspaceId: string,
  companyId: string,
): Promise<AccountingConnection[]> => {
  const response = await apiGet(
    token,
    `${connectionPath}?companyId=${encodeURIComponent(companyId)}`,
    workspaceId,
  );
  return (response?.data as AccountingConnection[]) ?? [];
};

export const connectTripletex = async (
  token: string,
  workspaceId: string,
  payload: ConnectTripletexPayload,
): Promise<AccountingConnection> => {
  const response = await apiPost(
    token,
    `${connectionPath}/tripletex`,
    payload,
    workspaceId,
  );
  return response?.data as AccountingConnection;
};

export const testAccountingConnection = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<AccountingConnection> => {
  const response = await apiPost(
    token,
    `${connectionPath}/${encodeURIComponent(connectionId)}/test`,
    {},
    workspaceId,
  );
  return response?.data as AccountingConnection;
};

export const configureTripletexWebhooks = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<AccountingConnection> => {
  const response = await apiPost(
    token,
    `${connectionPath}/${encodeURIComponent(connectionId)}/webhooks`,
    { confirm: true },
    workspaceId,
  );
  return response?.data as AccountingConnection;
};

export const disconnectAccountingConnection = async (
  token: string,
  workspaceId: string,
  connectionId: string,
): Promise<AccountingConnection> => {
  const response = await apiDelete(
    token,
    `${connectionPath}/${encodeURIComponent(connectionId)}`,
    workspaceId,
  );
  return response?.data as AccountingConnection;
};

export const getInvoiceAccountingDetails = async (
  token: string,
  workspaceId: string,
  invoiceId: string,
): Promise<AccountingInvoiceStatus> => {
  const response = await apiGet(
    token,
    `${invoicePath}/${encodeURIComponent(invoiceId)}`,
    workspaceId,
  );
  return response?.data as AccountingInvoiceStatus;
};

export const downloadAccountingInvoiceDocument = async (
  token: string,
  workspaceId: string,
  invoiceId: string,
): Promise<Blob> =>
  apiGetBlob(
    token,
    `${invoicePath}/${encodeURIComponent(invoiceId)}/document`,
    workspaceId,
  );

export const previewAccountingInvoice = async (
  token: string,
  workspaceId: string,
  invoiceId: string,
  allowCustomerCreate = false,
  finalizeDraft = false,
): Promise<AccountingInvoicePreview> => {
  const response = await apiPost(
    token,
    `${invoicePath}/${encodeURIComponent(invoiceId)}/preview`,
    { allowCustomerCreate, finalizeDraft },
    workspaceId,
  );
  return response?.data as AccountingInvoicePreview;
};

export const submitAccountingInvoice = async (
  token: string,
  workspaceId: string,
  invoiceId: string,
  allowCustomerCreate: boolean,
  finalizeDraft = false,
): Promise<AccountingInvoiceSubmission> => {
  const response = await apiPost(
    token,
    `${invoicePath}/${encodeURIComponent(invoiceId)}/submit`,
    { allowCustomerCreate, confirm: true, finalizeDraft },
    workspaceId,
  );
  return response?.data as AccountingInvoiceSubmission;
};

export const reconcileAccountingInvoice = async (
  token: string,
  workspaceId: string,
  invoiceId: string,
): Promise<AccountingInvoiceSubmission> => {
  const response = await apiPost(
    token,
    `${invoicePath}/${encodeURIComponent(invoiceId)}/reconcile`,
    {},
    workspaceId,
  );
  return response?.data as AccountingInvoiceSubmission;
};
